<template>
  <el-dialog
    :title="material ? '编辑素材标签' : '添加素材标签'"
    v-model="dialogVisible"
    width="700px"
    :before-close="handleClose"
  >
    <div class="tag-container">
      <div v-for="tag in tags" :key="tag.id" class='tag-parent'>
        <div v-if="tag.children && tag.children.length > 0" 
             :class="['tag-name', { 'highlight': isTagHighlighted(tag) }]"
             @click="toggleTagHighlight(tag)">{{ tag.tag_name }}</div>
        <el-button v-else 
                  plain 
                  :type="isTagHighlighted(tag) ? 'primary' : 'info'" 
                  size="small"
                  @click="toggleTagHighlight(tag)">{{ tag.tag_name }}</el-button>
        <div v-if="tag.children && tag.children.length > 0" :class="['tag-child', tag.tag_code]">
          <el-button v-for="child in tag.children" 
                    :key="child.id" 
                    plain 
                    :type="isTagHighlighted(child) ? 'primary' : 'info'" 
                    size="small" 
                    class="tag-child"
                    @click="toggleTagHighlight(child)">
            {{ child.tag_name }}
          </el-button>
        </div>
      </div>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="loading">
          确定
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import {saveTagsOne } from "../../api/material";



const props = defineProps<{
  visible: boolean
  tags: any | null
  material: any | null
}>()

const emit = defineEmits(['update:visible', 'success'])

const dialogVisible = ref(props.visible)
const loading = ref(false)

// 获取素材已有的标签ID集合
const materialTagIds = computed(() => {
  if (!props.material?.tags || !Array.isArray(props.material.tags)) {
    return new Set<number>()
  }
  return new Set(props.material.tags.map(tag => tag.id))
})

// 存储用户选择的标签ID
const selectedTagIds = ref<Set<number>>(new Set())

// 初始化时，将素材已有的标签设置为选中状态
watch(() => props.material, (newMaterial) => {
  if (newMaterial?.tags && Array.isArray(newMaterial.tags)) {
    selectedTagIds.value = new Set(newMaterial.tags.map(tag => tag.id))
  } else {
    selectedTagIds.value = new Set()
  }
}, { immediate: true })

// 检查标签是否需要高亮
const isTagHighlighted = (tag: any) => {
  return selectedTagIds.value.has(tag.id)
}

// 切换标签高亮状态
const toggleTagHighlight = (tag: any) => {
  if (selectedTagIds.value.has(tag.id)) {
    selectedTagIds.value.delete(tag.id)
  } else {
    selectedTagIds.value.add(tag.id)
  }
}

// 监听visible变化
watch(() => props.visible, (val) => {
  dialogVisible.value = val
})

watch(() => dialogVisible.value, (val) => {
  emit('update:visible', val)
})

// 处理关闭
const handleClose = () => {
  dialogVisible.value = false
}

// 处理提交
const handleSubmit = async () => {
  try {
    loading.value = true
    // 获取所有选中的标签ID数组
    const selectedTagsArray = Array.from(selectedTagIds.value)
    // 这里可以添加标签处理逻辑，使用selectedTagsArray进行提交
    const response = await saveTagsOne({
      material_id: props.material.id,
      tag_ids: selectedTagsArray
    })
    if (response.data.code == 200) {
      ElMessage.success(`打标签成功`);
      // 通知父组件刷新素材列表
      emit('save')
    } else {
      throw new Error(response.data.message || "打标签失败");
    }    
    dialogVisible.value = false
  } catch (error: any) {
    ElMessage.error(error.message || '操作失败')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.tag-container {
  max-height: 400px;
  overflow-y: auto;
}

.tag-parent {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 0;
}

.tag-name {
  font-size: 14px;
  color: #303133;
  padding-bottom: 10px;
  font-weight: 500;
}

.tag-child {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* 标签高亮样式 */
.highlight {
  color: var(--el-color-primary);
  font-weight: 600;
}
</style>